/**
 * Derivación dinámica de los textos emocionales y las tres características
 * del hero V2. TODO sale de los datos reales del producto (destino,
 * categorías, destacados, descripción). Si el tour no aporta una señal,
 * simplemente se muestran menos elementos: nunca se inventa contenido.
 */
import { stripHtml } from "@/lib/slug";

export type FeatureIcon =
  
  | "pyramid"
  | "waves"
  | "ship"
  | "leaf"
  | "fish"
  | "landmark"
  | "food"
  | "droplets"
  | "bird"
  | "bus"
  | "guide"
  | "camera"
  | "sun";

export interface TourFeature {
  icon: FeatureIcon;
  /** Clave i18n del rótulo (namespace tourV2.features). */
  key: string;
}

/** Palabras reales del producto → característica. Orden = prioridad. */
const RULES: { key: string; icon: FeatureIcon; terms: string[] }[] = [
  {
    key: "boat",
    icon: "ship",
    terms: ["lancha", "barco", "catamarán", "catamaran", "boat", "navega", "velero"],
  },
  { key: "sea", icon: "waves", terms: ["playa", "mar ", "caribe", "turquesa", "beach", "isla"] },
  { key: "snorkel", icon: "fish", terms: ["snorkel", "esnórquel", "buceo", "arrecife", "reef"] },
  {
    key: "nature",
    icon: "leaf",
    terms: ["naturaleza", "manglar", "reserva", "selva", "nature", "ecológ"],
  },
  {
    key: "wildlife",
    icon: "bird",
    terms: ["flamenco", "tortuga", "ballena", "tiburón", "aves", "fauna", "delfín"],
  },
  { key: "cenote", icon: "droplets", terms: ["cenote", "ojo de agua"] },
  {
    key: "culture",
    icon: "landmark",
    terms: [
      "arqueológ",
      "ruinas",
      "maya",
      "pueblo mágico",
      "museo",
      "cultura",
      "templo",
      "pirámide",
    ],
  },
  {
    key: "food",
    icon: "food",
    terms: ["comida", "gastronom", "almuerzo", "buffet", "degustación", "food"],
  },
  {
    key: "transport",
    icon: "bus",
    terms: ["transporte", "traslado", "pickup", "recogida", "van", "autobús"],
  },
  { key: "guide", icon: "guide", terms: ["guía", "guide", "certificado"] },
  { key: "photos", icon: "camera", terms: ["fotos", "fotograf", "photo"] },
  { key: "sunset", icon: "sun", terms: ["atardecer", "amanecer", "sunset"] },
];

export interface TourFactsInput {
  title: string;
  location: string | null;
  summary: string | null;
  description: string | null;
  categories: string[];
  /** Id del producto (para etiquetas fijadas). */
  id?: string | null;
  highlights: string[];
  included: string[];
  durationMinutes: number | null;
}

export interface TourFacts {
  /** Primer topónimo del destino real, p. ej. "Holbox" o "Cancún". */
  place: string | null;
  /** Frase corta derivada de la duración real. */
  fullDay: boolean;
  /** Descripción breve tomada del resumen/descripción reales. */
  shortDesc: string | null;
  features: TourFeature[];
}

function firstPlace(location: string | null, title: string): string | null {
  // El destino del título del producto es más preciso que la ciudad de salida
  // ("Tour a Holbox Express" sale de Cancún, pero el destino es Holbox).
  const m =
    /(?:tour|excursión|excursion|visita|trip|tour a|to)\s+(?:a|al|en|de|to)?\s*([\p{L}\s]{3,24}?)\s*(?:express|premium|deluxe|vip|$|[,.\-–])/iu.exec(
      title.trim(),
    );
  const fromTitle = m?.[1]?.trim();
  if (fromTitle && fromTitle.split(/\s+/).length <= 3) return fromTitle;
  const raw = (location ?? "").split(/[,·–-]/)[0]?.trim();
  return raw || null;
}

function firstSentence(text: string, max = 120): string | null {
  const clean = stripHtml(text).replace(/\s+/g, " ").trim();
  if (!clean) return null;
  const end = clean.search(/[.!?]\s/);
  const s = end > 20 ? clean.slice(0, end + 1) : clean;
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s;
}

/**
 * Etiquetas fijadas a mano por producto Bókun (sustituyen a las detectadas).
 * Solo afecta a los ids listados.
 */
const FEATURE_OVERRIDES: Record<string, TourFeature[]> = {
  "1237816": [
    { key: "archaeology", icon: "pyramid" },
    { key: "cenote", icon: "droplets" },
    { key: "culture", icon: "landmark" },
  ],
};

/** Productos cuyo encabezado de precios dice "Precios" (sin "desde"). */
const PRICES_HEADING_WITHOUT_FROM = new Set<string>(["1237816"]);
export function pricesHeadingKey(id: string | null | undefined): "tourV2.prices" | "tourV2.pricesFrom" {
  return id && PRICES_HEADING_WITHOUT_FROM.has(id) ? "tourV2.prices" : "tourV2.pricesFrom";
}

export function deriveTourFacts(input: TourFactsInput): TourFacts {
  const haystack = [
    input.title,
    input.summary ?? "",
    input.description ?? "",
    ...input.categories,
    ...input.highlights,
    ...input.included,
  ]
    .map((s) => stripHtml(s).toLowerCase())
    .join(" · ");

  const override = input.id ? FEATURE_OVERRIDES[input.id] : undefined;
  const features: TourFeature[] = override ? [...override] : [];
  if (!override) for (const rule of RULES) {
    if (features.length >= 3) break;
    if (rule.terms.some((term) => haystack.includes(term))) {
      features.push({ icon: rule.icon, key: rule.key });
    }
  }

  return {
    place: firstPlace(input.location, input.title),
    fullDay: (input.durationMinutes ?? 0) >= 360,
    shortDesc: firstSentence(input.summary || input.description || ""),
    features,
  };
}
